import { useCallback, useEffect, useState } from "react"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable } from "primereact/datatable"
import { ProgressSpinner } from "primereact/progressspinner"
import {
    closeLiquidation,
    getLiquidation,
    getLiquidationProfessionals,
    getLiquidationProfessionalsTotals,
    getLiquidationReceiptForms,
} from "../../servicesHooks/Honorarium/useHonorariumLiquidationService"
import { parseApiError } from "../../services/apiError"
import {
    HonorariumLiquidationDTO,
    HonorariumLiquidationProfessionalDTO,
    HonorariumLiquidationProfessionalsTotalsDTO,
    HonorariumLiquidationStatusId,
} from "../../dto/HonorariumLiquidationDTO"
import { formatMoney } from "../../utils/money"
import { formatDateOnly } from "../../utils/date"
import useNotify from "../../hooks/useNotify"
import useReceiptSummaryDownload from "../../hooks/useReceiptSummaryDownload"
import { confirmAction } from "../../components/ConfirmDelete/confirmDelete"
import Kpi from "../../components/Kpi/Kpi"
import ReadonlyField from "../../components/ReadonlyField/ReadonlyField"
import ReceiptFormsDialog from "../../components/ReceiptFormsDialog/ReceiptFormsDialog"
import LiquidationProfessionalRowActions from "./LiquidationProfessionalRowActions"
import RegisterPaymentDialog from "./RegisterPaymentDialog"
import AddItemsDialog from "./AddItemsDialog"
import LiquidationProfessionalStatusTag from "./LiquidationProfessionalStatusTag"
import LiquidationStatusTag from "./LiquidationStatusTag"

interface HonorariumLiquidationDetailViewProps {
    liquidationId: number;
    onBack: () => void;
    onSelectProfessional: (professional: HonorariumLiquidationProfessionalDTO) => void;
}

/** Detalle de una liquidación (DS §3.7): cabecera, totales y profesionales. Todos los montos los calcula el backend. */
export default function HonorariumLiquidationDetailView({
    liquidationId,
    onBack,
    onSelectProfessional,
}: HonorariumLiquidationDetailViewProps) {
    const notify = useNotify();
    const summary = useReceiptSummaryDownload();

    const [liquidation, setLiquidation] = useState<HonorariumLiquidationDTO | null>(null);
    const [professionals, setProfessionals] = useState<HonorariumLiquidationProfessionalDTO[]>([]);
    const [totals, setTotals] = useState<HonorariumLiquidationProfessionalsTotalsDTO | null>(null);
    // Profesional del que se ven los comprobantes (comprobante interno y sus pagos)
    const [formsProfessional, setFormsProfessional] = useState<HonorariumLiquidationProfessionalDTO | null>(null);
    // Pago desde la fila, sin entrar al detalle del profesional: el mismo diálogo que adentro
    const [paying, setPaying] = useState<HonorariumLiquidationProfessionalDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [addItemsVisible, setAddItemsVisible] = useState(false);
    const [closing, setClosing] = useState(false);

    /** `silent`: recarga después de una acción, sin tapar la pantalla con el spinner. */
    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [liquidationData, professionalsData, totalsData] = await Promise.all([
                getLiquidation(liquidationId),
                getLiquidationProfessionals(liquidationId),
                getLiquidationProfessionalsTotals(liquidationId),
            ]);
            setLiquidation(liquidationData);
            setProfessionals(professionalsData);
            setTotals(totalsData);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            if (apiError.status === 404) {
                notify.error(apiError.message);
                onBack();
                return;
            }
            notify.error('No se pudo cargar la liquidación. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [liquidationId, notify, onBack]);

    useEffect(() => { load(); }, [load]);

    const isOpen = liquidation?.statusId === HonorariumLiquidationStatusId.OPEN;
    // Saldo a favor: lo que la institución le debe por otros comprobantes de la cuenta corriente
    const hasCredit = (totals?.totalAccountCredit ?? 0) > 0;
    const hasManualCredits = (totals?.totalManualCredits ?? 0) > 0;
    // Retenciones por facturación propia: solo si algún profesional factura por su cuenta
    const hasRetentions = (totals?.totalRetentions ?? 0) !== 0;

    function summaryFileName(professional: HonorariumLiquidationProfessionalDTO): string {
        return `resumen-liquidacion-${liquidation?.liquidationNumber ?? ''}-${professional.professionalName}.pdf`;
    }

    function handleClose() {
        if (!liquidation || !totals) return;
        const count = professionals.length;
        const debt = totals.totalAccountDebt > 0
            ? ` Se descuentan ${formatMoney(totals.totalAccountDebt)} de deuda de cuenta corriente.`
            : '';
        const chargesExcess = totals.totalAdminChargesExcess > 0
            ? ` Los gastos y deducciones manuales que superan lo que cobran (${formatMoney(totals.totalAdminChargesExcess)}) se descuentan del saldo a favor`
                + ' y lo que no alcance queda como deuda en la cuenta corriente.'
            : '';
        const credit = totals.totalAccountCredit > 0
            ? ` El saldo a favor (${formatMoney(totals.totalAccountCredit)}) se puede pagar junto con esta liquidación.`
            : '';
        confirmAction({
            header: '¿Cerrar liquidación?',
            message: `Se van a liquidar ${count === 1 ? '1 profesional' : `${count} profesionales`} por un neto de ${formatMoney(totals.totalNet)}.${debt}${chargesExcess}${credit}`
                + ' Después no se puede modificar.',
            acceptLabel: 'Cerrar liquidación',
            onAccept: async () => {
                setClosing(true);
                try {
                    await closeLiquidation(liquidation.honorariumLiquidation);
                    notify.success(`Liquidación N° ${liquidation.liquidationNumber} cerrada`);
                    load(true);
                } catch (error: unknown) {
                    const apiError = parseApiError(error);
                    const isBusinessError = apiError.status >= 400 && apiError.status < 500;
                    notify.error(isBusinessError ? apiError.message : 'No se pudo cerrar la liquidación. Intentá de nuevo.');
                } finally {
                    setClosing(false);
                }
            },
        });
    }

    function renderRowActions(row: HonorariumLiquidationProfessionalDTO) {
        // Cerrada puede tener tres acciones (pagar, comprobantes, detalle): van en un menú ⋮ (DS §3.1)
        if (!isOpen) {
            return (
                <div className="flex justify-content-center">
                    <LiquidationProfessionalRowActions
                        professional={row}
                        onRegisterPayment={setPaying}
                        onViewForms={setFormsProfessional}
                        onDownloadSummary={p => summary.download(p.internalReceiptForm!, summaryFileName(p))}
                        onViewDetail={onSelectProfessional}
                    />
                </div>
            );
        }
        return (
            <div className="flex justify-content-center">
                <Button
                    text
                    rounded
                    icon="pi pi-eye"
                    className="md-icon-btn"
                    aria-label={`Ver detalle de ${row.professionalName}`}
                    tooltip="Ver detalle"
                    tooltipOptions={{ position: 'left' }}
                    onClick={() => onSelectProfessional(row)}
                />
            </div>
        );
    }

    const money = (value: number | null | undefined) => formatMoney(value);

    return (
        <div className="liquidation-detail">
            <div>
                <Button text icon="pi pi-angle-double-left" label="Volver" className="md-back-link" onClick={onBack} />
            </div>

            <div className="md-header-row liquidation-detail__header">
                <div>
                    <h1 className="md-page-title">
                        Liquidación{liquidation ? ` N° ${liquidation.liquidationNumber}` : ''}
                    </h1>
                    {liquidation && (
                        <p className="md-entity-subtitle liquidation-detail__subtitle">
                            {liquidation.billingEntityName}
                            <LiquidationStatusTag statusId={liquidation.statusId} status={liquidation.status} />
                        </p>
                    )}
                </div>
                {isOpen && (
                    <div className="md-dialog__footer">
                        <Button
                            outlined
                            icon="pi pi-plus-circle"
                            label="Agregar ítems"
                            className="md-ghost"
                            onClick={() => setAddItemsVisible(true)}
                        />
                        <Button label="Cerrar liquidación" className="md-primary" onClick={handleClose} loading={closing} />
                    </div>
                )}
            </div>

            {loading && (
                <div className="md-loading" role="status">
                    <ProgressSpinner strokeWidth="4" />
                    <span>Cargando…</span>
                </div>
            )}

            {!loading && liquidation && totals && (
                <>
                    <div className="md-readonly-grid liquidation-detail__summary">
                        <ReadonlyField label="Período">
                            {`${formatDateOnly(liquidation.fromDate)} – ${formatDateOnly(liquidation.toDate)}`}
                        </ReadonlyField>
                        <ReadonlyField label="Descripción">{liquidation.description}</ReadonlyField>
                        <ReadonlyField label="Profesionales">{professionals.length}</ReadonlyField>
                    </div>

                    <div className="md-kpi-row liquidation-detail__kpis">
                        <Kpi label="Honorarios" value={money(totals.totalHonorarium)} />
                        <Kpi label="Retenciones" value={money(totals.totalWithholdings)} />
                        {hasManualCredits && <Kpi label="Créditos" value={money(totals.totalManualCredits)} />}
                        <Kpi label="Deducciones" value={money(totals.totalDeductions)} />
                        <Kpi label="Gastos administrativos" value={money(totals.totalAdminCharges)} />
                        {hasRetentions && (
                            <Kpi label="Retención fact. propia" value={money(totals.totalRetentions)} hint="De las órdenes que facturaron ellos" />
                        )}
                        {isOpen && totals.totalAccountDebt > 0 && (
                            <Kpi label="Deuda C/C a descontar" value={money(totals.totalAccountDebt)} hint="Se descuenta al cerrar" />
                        )}
                        <Kpi label="Neto a pagar" value={money(totals.totalNet)} hint={isOpen ? 'Proyectado al cierre' : undefined} />
                        {!isOpen && <Kpi label="Pagado" value={money(totals.totalPaymentOrders)} />}
                        {!isOpen && <Kpi label="Pendiente de pago" value={money(totals.totalPending)} />}
                        {hasCredit && (
                            <Kpi label="Saldo a favor C/C" value={money(totals.totalAccountCredit)} hint="De rendiciones o liquidaciones anteriores" />
                        )}
                    </div>

                    <h2 className="md-section-title liquidation-detail__section-title">Profesionales</h2>
                    <DataTable
                        value={professionals}
                        dataKey="honorariumLiquidationProfessional"
                        className="crudTable"
                        emptyMessage="La liquidación no tiene profesionales. Agregá ítems para liquidar."
                        onRowClick={(e) => onSelectProfessional(e.data as HonorariumLiquidationProfessionalDTO)}
                        rowClassName={() => 'liquidation-detail__row'}
                    >
                        <Column field="professionalName" header="Profesional" footer="Total" />
                        {!isOpen && (
                            <Column
                                header="Estado"
                                body={(row: HonorariumLiquidationProfessionalDTO) => (
                                    <LiquidationProfessionalStatusTag statusId={row.statusId} status={row.status} />
                                )}
                                className="text-center"
                                headerClassName="col-center"
                            />
                        )}
                        <Column header="Honorarios" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.totalAmount)}
                            footer={money(totals.totalHonorarium)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        <Column header="Retenciones" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.withholdingsTotal)}
                            footer={money(totals.totalWithholdings)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        {hasManualCredits && (
                            <Column header="Créditos" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.manualCreditsTotal)}
                                footer={money(totals.totalManualCredits)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        )}
                        <Column header="Deducciones" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.deductionsTotal)}
                            footer={money(totals.totalDeductions)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        <Column header="Gastos" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.adminChargesTotal)}
                            footer={money(totals.totalAdminCharges)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        {hasRetentions && (
                            <Column header="Ret. fact. propia" headerTooltip="Retenciones por facturación propia"
                                body={(row: HonorariumLiquidationProfessionalDTO) => money(row.retentionsTotal)}
                                footer={money(totals.totalRetentions)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        )}
                        {isOpen && (
                            <Column header="Deuda C/C" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.accountDebt)}
                                footer={money(totals.totalAccountDebt)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        )}
                        <Column header="Neto a pagar" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.netToPay)}
                            footer={money(totals.totalNet)} className="text-right liquidation-detail__amount" headerClassName="col-right" footerClassName="text-right" />
                        {!isOpen && (
                            <Column header="Pendiente" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.pendingAmount)}
                                footer={money(totals.totalPending)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        )}
                        {hasCredit && (
                            <Column header="A favor C/C" body={(row: HonorariumLiquidationProfessionalDTO) => money(row.accountCredit)}
                                footer={money(totals.totalAccountCredit)} className="text-right" headerClassName="col-right" footerClassName="text-right" />
                        )}
                        <Column header="" body={renderRowActions} style={{ width: '64px' }} />
                    </DataTable>
                </>
            )}

            <RegisterPaymentDialog
                professional={paying}
                liquidationNumber={liquidation?.liquidationNumber ?? 0}
                onHide={() => setPaying(null)}
                onRegistered={() => {
                    setPaying(null);
                    load(true);
                }}
            />

            <ReceiptFormsDialog
                visible={formsProfessional != null}
                header={formsProfessional ? `Comprobantes · ${formsProfessional.professionalName}` : 'Comprobantes'}
                load={() => getLiquidationReceiptForms(formsProfessional!.honorariumLiquidationProfessional)}
                summaryFileName={formsProfessional ? summaryFileName(formsProfessional) : 'resumen-liquidacion.pdf'}
                onHide={() => setFormsProfessional(null)}
            />

            {liquidation && (
                <AddItemsDialog
                    visible={addItemsVisible}
                    liquidationId={liquidation.honorariumLiquidation}
                    onHide={() => setAddItemsVisible(false)}
                    onAdded={() => {
                        setAddItemsVisible(false);
                        load(true);
                    }}
                />
            )}
        </div>
    );
}
